// src/pages/Casting/CastingPage.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Search } from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import CastingCallCard from '../../components/Studio/CastingCallCard';
import FilterChips from '../../components/Studio/FilterChips';
import {
  StudioCallout,
  StudioEmptyState,
  StudioErrorState,
} from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  INPUT,
  PANEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import castingService from '../../services/castingService';

const TYPE_FILTERS = [
  { value: '', label: 'All calls' },
  { value: 'CAST', label: 'Cast' },
  { value: 'CREW', label: 'Crew' },
  { value: 'COLLABORATOR', label: 'Collaborators' },
];

const PAGE_SIZE = 24;

const CastingPage = () => {
  useSEO({
    title: 'Open Calls — Casting at StrymHub Studios',
    description:
      'Current casting calls and crew positions on StrymHub Studios productions. Members apply directly — no agent, no gatekeeper.',
    keywords:
      'casting calls uk, sheffield casting, film crew jobs, open call actors, indie film casting, apply for film role',
    type: 'website',
  });

  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(0);

  const calls = useStudioResource(
    (signal) =>
      castingService.getOpenCalls(
        { type: type || undefined, search: submittedSearch || undefined, page, size: PAGE_SIZE },
        signal,
      ),
    [type, submittedSearch, page],
  );

  const results = calls.data;
  const isFiltered = Boolean(type || submittedSearch);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmittedSearch(search.trim());
    setPage(0);
  };

  return (
    <StudioShell>
      <StudioHero
        eyebrow="Casting at StrymHub Studios"
        title="We cast from"
        accent="our own community."
        lede="Every role and every crew position on a StrymHub production goes out here first. No agents, no submission fees, no black-hole inbox — you apply, and a person reads it."
        stats={results ? [{ label: 'Open calls', value: String(results.totalElements) }] : undefined}
        actions={
          <>
            <Link to="/community/join" className={PRIMARY_BUTTON}>
              Join the troupe first
            </Link>
            <Link to="/casting/my-applications" className={SECONDARY_BUTTON}>
              My applications
            </Link>
          </>
        }
      />

      <div className={`${CONTAINER} py-12`}>
        <form onSubmit={handleSearchSubmit} className="mb-8">
          <label htmlFor="casting-search" className="sr-only">
            Search open calls
          </label>
          <div className="relative max-w-lg">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500"
              aria-hidden="true"
            />
            <input
              id="casting-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by role, title or location"
              className={`${INPUT} pl-11`}
            />
          </div>
        </form>

        <FilterChips
          label="Filter open calls by type"
          options={TYPE_FILTERS}
          value={type}
          onChange={(value) => {
            setType(value);
            setPage(0);
          }}
        />

        <div className="mt-10">
          {calls.loading && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div key={index} className={`${PANEL} h-56 animate-pulse`} />
              ))}
            </div>
          )}

          {!calls.loading && calls.error && (
            <StudioErrorState message={calls.error} onRetry={calls.reload} />
          )}

          {!calls.loading && !calls.error && results && results.content.length === 0 && (
            <StudioEmptyState
              icon={<Megaphone className="h-8 w-8" />}
              title={isFiltered ? 'Nothing open in that category' : 'No open calls right now'}
              description={
                isFiltered
                  ? 'Try another category, or check back — calls open as each production moves into casting.'
                  : "Calls go out as each production moves into casting. Join the troupe and you'll be in the pool the moment the next one opens."
              }
              action={
                <Link to="/community/join" className={PRIMARY_BUTTON}>
                  Join the troupe
                </Link>
              }
            />
          )}

          {!calls.loading && !calls.error && results && results.content.length > 0 && (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {results.content.map((call) => (
                  <CastingCallCard key={call.id} call={call} />
                ))}
              </div>

              {results.totalPages > 1 && (
                <nav
                  aria-label="Casting calls pagination"
                  className="mt-12 flex items-center justify-center gap-4"
                >
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    disabled={results.first}
                    className={SECONDARY_BUTTON}
                  >
                    Previous
                  </button>
                  <p aria-live="polite" className="text-sm text-neutral-400">
                    Page {results.number + 1} of {results.totalPages}
                  </p>
                  <button
                    type="button"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={results.last}
                    className={SECONDARY_BUTTON}
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </div>

        <div className="mt-16">
          <StudioCallout
            title="Nothing here for you today?"
            description="Join the troupe anyway. When we cast, we search the troupe before we write a call — a lot of roles never make it to this page."
            action={
              <Link to="/community/join" className={PRIMARY_BUTTON}>
                Join the troupe
              </Link>
            }
          />
        </div>
      </div>
    </StudioShell>
  );
};

export default CastingPage;
